import { useParams } from "react-router-dom";
import { useAppState } from "@/modules/app/useAppContext";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { useQuery } from "@tanstack/react-query";
import { getDbtJobListing, getDbtJobListingSummary, getDbtJobFilters } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { formatBytes, formatCost, formatMilliseconds } from "@/helpers/utils";
import { Stack, Alert, AlertDescription, AlertTitle } from "@altimateai/lego";
import { FlexListSummary } from "@/components/listSummary";
import { DataTable } from "@/components/tables";
import { DbtJobListingItem } from "@/pages/query/types";
import dayjs from "dayjs";
import { ExecutionStatusCount } from "@/pages/query/ExecutionStatusCount";
import { GET_DBT_JOB_LISTING, GET_DBT_JOB_LISTING_SUMMARY } from "@/pages/query/constants";
import { Paginate } from "@/components/paginate";
import { useDbtJobListingParams } from "@/pages/query/hooks/useDbtJobListingParams";
import { useTablePagination } from "@/components/tables/hooks/useTablePagination";
import { DbtCommandHighlight } from "./DbtCommandHighlight";
import { Tooltip } from "@altimateai/lego";
import { FilterType } from "@/components/tables/types";
import { FilterApplicableFor } from "@/components/tags/types";
import { useDatatableContext } from "@/components/tables/contexts/DatatableContextProvider";
import { useMemo } from "react";

const ONE_HOUR = 60 * 60 * 1000;

const useJobListingHeaders = (currency: string, encodedWorkflowId: string) => [
  {
    id: 1,
    label: "Command",
    uid: "command_display",
    width: "25%" as const,
    filter: {
      filterType: FilterType.API_DROPDOWN as FilterType.API_DROPDOWN,
      searchKey: "command",
      label: "Job",
      apiFunction: (params: { prefix?: string; page?: number; size?: number }) =>
        getDbtJobFilters(encodedWorkflowId, {
          page: params.page ?? 1,
          size: params.size ?? 50,
          prefix: params.prefix,
        }),
      extractorFunction: (data: unknown) =>
        ((data as { items: { command_display: string }[] }).items ?? []).map(item => ({
          label: item.command_display,
          value: item.command_display,
          showTooltip: true,
        })),
      seeMore: true,
      pageSize: 50,
    },
    render: (item: DbtJobListingItem) => (
      <Tooltip
        title={
          <div className="al-max-w-lg al-break-words al-whitespace-pre-wrap">
            <DbtCommandHighlight command={item.command_display} />
          </div>
        }
      >
        <div className="al-truncate al-overflow-hidden">
          <DbtCommandHighlight command={item.command_display} />
        </div>
      </Tooltip>
    ),
  },
  {
    id: 2,
    label: "Type",
    uid: "command_which",
    render: (item: DbtJobListingItem) => (
      <span className="al-capitalize">{item.command_which}</span>
    ),
  },
  {
    id: 3,
    label: "Total Cost",
    uid: "query_cost",
    sort: { sortSearchKey: "sortAttribute" },
    render: (item: DbtJobListingItem) => <div>{formatCost(item.query_cost, currency)}</div>,
  },
  {
    id: 4,
    label: "Bytes Scanned",
    uid: "bytes_scanned",
    sort: { sortSearchKey: "sortAttribute" },
    render: (item: DbtJobListingItem) => <div>{formatBytes(item.bytes_scanned)}</div>,
  },
  {
    id: 5,
    label: "Avg Execution Time",
    uid: "execution_time",
    sort: { sortSearchKey: "sortAttribute" },
    render: (item: DbtJobListingItem) => <div>{formatMilliseconds(item.execution_time)}</div>,
  },
  {
    id: 6,
    label: "Run Count",
    uid: "run_count",
    sort: { sortSearchKey: "sortAttribute" },
    render: (item: DbtJobListingItem) => <div>{item.run_count}</div>,
  },
  {
    id: 7,
    label: "Status",
    uid: "success_rate",
    render: (item: DbtJobListingItem) => (
      <ExecutionStatusCount run_count={item.run_count} success_count={item.success_count} />
    ),
  },
  {
    id: 8,
    label: "Last Run",
    uid: "last_runtime",
    sort: { sortSearchKey: "sortAttribute" },
    render: (item: DbtJobListingItem) => (
      <div>{dayjs(item.last_runtime).format("YYYY-MM-DD hh:mm a")}</div>
    ),
  },
];

const DbtJobListingContent = () => {
  const { workflowId } = useParams();
  const { currency } = useAppState();
  const { startDate, endDate } = useDateFromSearchParams();
  const { page, setPage, pageSize, setPageSize, resetPage } = useTablePagination();
  const { sortAttribute, sortOrder } = useDbtJobListingParams();
  const { getFilterValue } = useDatatableContext();
  const encodedWorkflowId = workflowId ? encodeURIComponent(workflowId) : "";
  const header = useJobListingHeaders(currency, encodedWorkflowId);

  const commandFilter = useMemo(() => getFilterValue<string[]>("command") || [], [getFilterValue]);

  const dateParams = {
    start_date: getDateByStartOfDayWithoutTimeoffset(startDate).toISOString(),
    end_date: getDateByEndOfDayWithoutTimeoffset(endDate)?.toISOString(),
  };

  const {
    data: jobs,
    isLoading: isJobsLoading,
    error: jobsError,
    isError: isJobsError,
  } = useQuery({
    queryKey: [
      GET_DBT_JOB_LISTING,
      workflowId,
      startDate,
      endDate,
      page,
      pageSize,
      sortAttribute,
      sortOrder,
      commandFilter,
    ],
    queryFn: () =>
      getDbtJobListing(encodedWorkflowId, {
        ...dateParams,
        page: page + 1,
        size: pageSize,
        sortAttribute,
        sortOrder,
        command: commandFilter,
      }),
    enabled: Boolean(workflowId),
    gcTime: ONE_HOUR,
  });

  const totalItems = jobs?.total ?? 0;
  const totalPages = jobs?.pages ?? 0;

  const { data: summaryData, isLoading: isSummaryLoading } = useQuery({
    queryKey: [GET_DBT_JOB_LISTING_SUMMARY, workflowId, startDate, endDate],
    queryFn: () => getDbtJobListingSummary(encodeURIComponent(workflowId!), dateParams),
    enabled: Boolean(workflowId),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });

  const summaryItems = summaryData
    ? [
        { label: "Total Jobs", value: String(summaryData.total_jobs) },
        { label: "Total Cost", value: formatCost(summaryData.total_cost, currency) },
        { label: "Total Runs", value: String(summaryData.total_runs) },
      ]
    : [];

  return (
    <div role="region" aria-label="dbt job listing region">
      <FlexListSummary summary={summaryItems} isLoading={isSummaryLoading} skeletonCount={3} />
      <Stack justify="end" className="al-py-2">
        <div id="dbt-tab-filter" />
      </Stack>
      <Stack direction="column" gap="md">
        {isJobsError ? (
          <Alert variant="destructive" role="alert">
            <AlertTitle>Error loading jobs</AlertTitle>
            <AlertDescription>
              {jobsError instanceof Error
                ? jobsError.message
                : "Failed to load jobs. Please try again."}
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <DataTable
              isLoading={isJobsLoading}
              resetPage={resetPage}
              data={{ items: jobs?.items ?? [] }}
              defaultSort={{ sortParamValue: "query_cost", order: "desc" }}
              applicableFor={FilterApplicableFor.DBT_JOBS}
              portals={{ filter: "dbt-tab-filter" }}
              header={header}
            />
            <Paginate
              isLoading={isJobsLoading}
              itemCount={totalItems}
              page={page}
              pageSize={pageSize}
              numPages={totalPages}
              onPageClick={setPage}
              onPageSizeChange={setPageSize}
              showPageSize
            />
          </>
        )}
      </Stack>
    </div>
  );
};

export { DbtJobListingContent };
